import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const features = {
  "precision-clean": [
    "Full interior + exterior",
    "Hand wash + clay decontamination",
    "Deep vacuum + carpet shampoo",
    "Trim + leather conditioning",
    "Premium wax / sealant",
  ],
  "interior-detailing": [
    "Seats, carpets, panels, trim",
    "Deep extraction shampoo",
    "Glass + dashboard restoration",
    "Odor neutralization",
  ],
  "exterior-detailing": [
    "Hand wash + foam pre-soak",
    "Iron + tar decontamination",
    "Wheels, tires + trim dressing",
    "Sealant for hydrophobic finish",
  ],
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState({});

  useEffect(() => { api.get("/services").then(r => setServices(r.data)).catch(() => {}); }, []);

  const getPrice = (s) => {
    if (!s.has_vehicle_types) return s.price;
    const sel = selectedVehicle[s.id];
    if (!sel) return s.price;
    const vt = s.vehicle_types.find((v) => v.type === sel);
    return vt ? vt.price : s.price;
  };

  return (
    <div data-testid="services-page">
      <section className="pt-24 pb-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
            Built for the<br/>shine you want.
          </h1>
          <p className="mt-6 max-w-2xl text-[#A3A3A3]">Pick the package, pick your time. Transparent pricing. Premium results.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.id} className="bg-[#121212] border border-[#262626] p-8 flex flex-col hover:border-[#D90429]/60 transition-colors speed-line" data-testid={`service-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <Flame className="text-[#D90429]" />
                <div className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">Pkg. 0{s.order}</div>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight">{s.name}</h3>
              <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">{s.description}</p>

              {s.has_vehicle_types && (
                <div className="mt-6">
                  <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Select Vehicle Type</label>
                  <Select
                    value={selectedVehicle[s.id] || ""}
                    onValueChange={(v) => setSelectedVehicle({ ...selectedVehicle, [s.id]: v })}
                  >
                    <SelectTrigger className="mt-2 bg-[#050505] border border-[#262626] focus:border-[#D90429] rounded-none h-12" data-testid={`vehicle-type-${s.id}`}>
                      <SelectValue placeholder="Choose vehicle" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#262626] rounded-none">
                      {s.vehicle_types.map((vt) => (
                        <SelectItem key={vt.type} value={vt.type} data-testid={`vehicle-type-${s.id}-${vt.type}`}>
                          {vt.type} — ${vt.price.toFixed(0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <ul className="mt-6 space-y-2 flex-1">
                {(features[s.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#D4D4D4]">
                    <Check size={16} className="text-[#D90429] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-[#262626] flex items-baseline justify-between">
                <span className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">
                  {s.has_vehicle_types && !selectedVehicle[s.id] ? "From" : "Total"}
                </span>
                <span className="font-display text-4xl font-black text-white">${getPrice(s).toFixed(0)}</span>
              </div>

              <a
                href={JOBBER_BOOKING_URL}
                data-testid={`book-service-${s.id}`}
                className="mt-6 block w-full bg-[#D90429] hover:bg-[#EF233C] text-white text-center font-display font-bold tracking-wider uppercase px-6 py-4 transition-colors"
              >
                Book This Service
              </a>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
